/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types';
import {
    Box, Typography, Stack, Select, MenuItem, TextField, alpha, FormControl,
    InputLabel,
} from '@mui/material';
import Iconify from 'src/components/Iconify';

/* ──────────────────────────────────────────────────────────────────────────
   BroadcastAttestationCard
   ────────────────────────────────────────
   GDPR lawful-basis attestation for PATIENT_LIST sends. The previous
   boolean "I am authorised" switch sent ``{user_confirmed, confirmed_at}``
   to the backend, which the SendFormsRequestSerializer rejects — the
   required shape is ``{lawful_basis, source_description}`` (see
   ``apps/practice/send_forms_serializers.py::validate_broadcast_attestation``).

   This component renders both fields with inline help so practice users
   can give a defensible answer and the broadcast actually goes through.

   Owned (controlled) by the dialog so the parent can validate before
   enabling the Generate-link CTA.
   ────────────────────────────────────────────────────────────────────────── */

const LAWFUL_BASIS_OPTIONS = [
    {
        value: 'consent',
        label: 'Patient consent',
        help: 'These patients have given explicit, recorded consent to receive marketing / outreach from us.',
    },
    {
        value: 'legitimate_interest',
        label: 'Legitimate interest',
        help: 'There is a relevant relationship (e.g. existing patient) and the message is reasonably expected.',
    },
];

const BroadcastAttestationCard = ({
    value, onChange, recipientCount,
}) => {
    const lawfulBasis = value?.lawful_basis || '';
    const sourceDescription = value?.source_description || '';
    const activeHelp = LAWFUL_BASIS_OPTIONS.find((o) => o.value === lawfulBasis)?.help || '';

    const update = (patch) => {
        onChange({
            lawful_basis: lawfulBasis,
            source_description: sourceDescription,
            ...patch,
        });
    };

    return (
        <Box
            sx={{
                p: 1.5, borderRadius: '10px',
                bgcolor: alpha('#FFAB00', 0.05),
                border: '1px solid', borderColor: alpha('#FFAB00', 0.25),
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Iconify icon="eva:shield-fill" width={16} sx={{ color: '#B76E00' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#B76E00' }}>
                    GDPR attestation
                </Typography>
                {recipientCount > 0 && (
                    <Typography sx={{ fontSize: '11px', color: '#637381', ml: 'auto' }}>
                        {`${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}
                    </Typography>
                )}
            </Stack>

            <Stack spacing={1.25}>
                <FormControl size="small" fullWidth>
                    <InputLabel id="lawful-basis-label" sx={{ fontSize: '12px' }}>
                        Lawful basis
                    </InputLabel>
                    <Select
                        labelId="lawful-basis-label"
                        label="Lawful basis"
                        value={lawfulBasis}
                        onChange={(e) => update({ lawful_basis: e.target.value })}
                        sx={{ fontSize: '12px', borderRadius: '8px', bgcolor: '#fff' }}
                    >
                        {LAWFUL_BASIS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '12px' }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {activeHelp && (
                    <Typography sx={{ fontSize: '10.5px', color: '#637381', lineHeight: 1.5 }}>
                        {activeHelp}
                    </Typography>
                )}

                <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder="How were these recipients sourced? (e.g. existing patients with marketing consent recorded on 2025-09-12 intake form)"
                    label="Source description"
                    value={sourceDescription}
                    onChange={(e) => update({ source_description: e.target.value })}
                    inputProps={{ maxLength: 1000, 'aria-label': 'GDPR source description' }}
                    helperText={`${sourceDescription.length}/1000`}
                    FormHelperTextProps={{ sx: { fontSize: '10px', textAlign: 'right', m: 0, mt: 0.25 } }}
                    sx={{ bgcolor: '#fff', borderRadius: '8px' }}
                />
            </Stack>
        </Box>
    );
};

BroadcastAttestationCard.propTypes = {
    value: PropTypes.shape({
        lawful_basis: PropTypes.string,
        source_description: PropTypes.string,
    }),
    onChange: PropTypes.func.isRequired,
    recipientCount: PropTypes.number,
};

BroadcastAttestationCard.defaultProps = {
    value: { lawful_basis: '', source_description: '' },
    recipientCount: 0,
};

export const isAttestationValid = (value) => {
    if (!value) return false;
    const lb = (value.lawful_basis || '').trim().toLowerCase();
    const sd = (value.source_description || '').trim();
    return ['consent', 'legitimate_interest'].includes(lb)
        && sd.length > 0
        && sd.length <= 1000;
};

export default BroadcastAttestationCard;
